import { supabase } from '../supabase';
import { GetFeedResult, PostRow, CommentWithAuthor, PostTagRow } from '../../types/database'; // Adicionar PostTagRow
// import { Database } from '../../types/supabase'; // Manter comentado por enquanto
import { processAction } from '../gamification/gamificationService';
import { XP_ACTIONS } from '../gamification/xpConfig';

// Manter comentado até os tipos serem gerados corretamente
/*
type GetFeedResult = Database['public']['Functions']['get_feed']['Returns'];
*/

// TODO: Definir tipos mais específicos para Post, Tag, etc., quando disponíveis

const UUID_NIL = '00000000-0000-0000-0000-000000000000';

// Função auxiliar para verificar se está offline
const isOffline = () => typeof navigator !== 'undefined' && !navigator.onLine;

// Cache para armazenar temporariamente os feeds
const feedsCache = new Map<string, {
  data: GetFeedResult[] | null;
  timestamp: number;
}>();

// Funções de cache
const getCachedFeed = (key: string) => {
  const cachedData = feedsCache.get(key);
  
  if (cachedData && Date.now() - cachedData.timestamp < 4 * 60 * 60 * 1000) { // 4 horas
    return cachedData.data;
  }
  
  return null;
};

const setCachedFeed = (key: string, data: GetFeedResult[] | null) => {
  feedsCache.set(key, {
    data,
    timestamp: Date.now()
  });
};

// Interface para a estrutura de uma tag no payload
interface PostTagPayload {
  type: 'game' | 'category' | 'mechanic' | 'theme';
  value: string; // game_id para 'game', tag_name para os outros
}

// Interface para o payload da criação de post - Atualizada
export interface CreatePostPayload {
  user_id: string;
  content_text: string | null;
  image_url?: string | null;
  video_url?: string | null;
  parent_post_id?: string | null;
  tags?: PostTagPayload[]; // <<< Aceitar o tipo mais genérico
}

// Interface para payload de addComment
export interface AddCommentPayload {
  post_id: string;
  user_id: string;
  content: string;
}

// Tipo para as opções de ordenação aceitas pelo serviço/RPC
type FeedSortOption = 'created_at_desc' | 'created_at_asc' | 'like_count_desc';

// --- Funções Exportadas --- 

/**
 * Adiciona um like a um post (Função interna, chamada por toggleLikePost).
 */
async function addLikeInternal(postId: string, userId: string): Promise<boolean> {
  if (!postId || !userId) {
    console.error('[FeedService:addLikeInternal] postId e userId são obrigatórios.');
    return false;
  }
  try {
    // Usar a RPC que já existe no Supabase (sem a lógica de XP dentro dela)
    const { data, error } = await supabase.rpc('add_like', { p_post_id: postId, p_user_id: userId });
    if (error) {
      console.error('[FeedService:addLikeInternal] Erro RPC add_like:', error);
      return false;
    }
    console.log('[FeedService:addLikeInternal] Resultado RPC add_like:', data);
    // Assumindo que a RPC retorna true em sucesso
    return data === true;
  } catch (err) {
    console.error('[FeedService:addLikeInternal] Exceção RPC add_like:', err);
    return false;
  }
}

/**
 * Remove um like de um post (Função interna, chamada por toggleLikePost).
 */
async function removeLikeInternal(postId: string, userId: string): Promise<boolean> {
  if (!postId || !userId) {
    console.error('[FeedService:removeLikeInternal] postId e userId são obrigatórios.');
    return false;
  }
  try {
    // Usar a RPC que já existe no Supabase (sem a lógica de XP dentro dela)
    const { data, error } = await supabase.rpc('remove_like', { p_post_id: postId, p_user_id: userId });
    if (error) {
      console.error('[FeedService:removeLikeInternal] Erro RPC remove_like:', error);
      return false;
    }
    console.log('[FeedService:removeLikeInternal] Resultado RPC remove_like:', data);
    // Assumindo que a RPC retorna true em sucesso
    return data === true;
  } catch (err) {
    console.error('[FeedService:removeLikeInternal] Exceção RPC remove_like:', err);
    return false;
  }
}

/**
 * Busca posts para o feed usando a função RPC otimizada `get_feed`.
 */
export async function getFeed(
    loggedUserId: string | null,
  filters: { 
      filterUserId?: string;
      tagType?: 'game' | 'mechanic' | 'category' | 'theme'; 
      tagValue?: string; 
      filterGameName?: string;
      sortBy?: FeedSortOption;
  } = {},
    page: number = 1,
    pageSize: number = 10
): Promise<GetFeedResult[] | null> {
    try {
    // Gerar chave de cache
    const cacheKey = `feed:${loggedUserId || 'guest'}:${filters.filterUserId || ''}:${filters.tagType || ''}:${filters.tagValue || ''}:${filters.filterGameName || ''}:${filters.sortBy || 'created_at_desc'}:${page}:${pageSize}`;
    
    // Verificar se está offline
    if (isOffline()) {
      console.log('[FeedService:getFeed] Dispositivo offline, tentando usar cache...');
      const cachedFeed = getCachedFeed(cacheKey);
      if (cachedFeed) {
        console.log('[FeedService:getFeed] Usando feed em cache');
        return cachedFeed;
      }
      console.log('[FeedService:getFeed] Nenhum cache encontrado para o feed');
      return []; // Retornar array vazio em vez de null para evitar erros de UI
    }
    
    // Preparar parâmetros para a RPC
    const rpcParams = {
        p_logged_user_id: loggedUserId || UUID_NIL,
        p_filter_user_id: filters.filterUserId || undefined,
        p_tag_type: filters.tagType || undefined,
        p_tag_value: filters.tagValue || undefined,
        p_filter_game_name: filters.filterGameName || undefined,
        p_sort_by: filters.sortBy || 'created_at_desc',
        p_page: page,
        p_page_size: pageSize
    };
    console.log('[FeedService:getFeed] Chamando RPC com filtros:', rpcParams);

    const { data, error } = await supabase.rpc('get_feed', rpcParams);

      if (error) {
      console.error('[FeedService:getFeed] Erro RPC:', error);
      // Verificar se temos um cache para esse feed
      const cachedFeed = getCachedFeed(cacheKey);
      if (cachedFeed) {
        console.log('[FeedService:getFeed] Usando feed em cache após erro');
        return cachedFeed;
      }
      
      // Se não tem cache, verificar se é erro de rede
      if (error.message.includes('Failed to fetch') || 
          error.message.includes('NetworkError') ||
          error.message.includes('ERR_FAILED')) {
        console.log('[FeedService:getFeed] Erro de rede, retornando array vazio');
        return []; // Retornar array vazio em vez de null para evitar erros de UI
      }
      
      throw error;
    }
    console.log(`[FeedService:getFeed] RPC retornou ${data?.length ?? 0} posts.`);
    
    // Armazenar em cache
    setCachedFeed(cacheKey, data as GetFeedResult[] | null);
    
    return data as GetFeedResult[] | null;
  } catch (err) {
    console.error('[FeedService:getFeed] Exceção:', err);
    // Se o erro for de rede (fetch), tentar usar cache
    if (err instanceof Error && (
      err.message.includes('Failed to fetch') || 
      err.message.includes('NetworkError') ||
      err.message.includes('ERR_FAILED') ||
      err.message.includes('network')
    )) {
      // Gerar chave de cache
      const cacheKey = `feed:${loggedUserId || 'guest'}:${filters.filterUserId || ''}:${filters.tagType || ''}:${filters.tagValue || ''}:${filters.filterGameName || ''}:${filters.sortBy || 'created_at_desc'}:${page}:${pageSize}`;
      
      const cachedFeed = getCachedFeed(cacheKey);
      if (cachedFeed) {
        console.log('[FeedService:getFeed] Usando feed em cache após erro de rede');
        return cachedFeed;
      }
    }
    
    return []; // Retornar array vazio em vez de null para evitar erros de UI
  }
}

/**
 * Cria um novo post e, opcionalmente, suas tags.
 */
export async function createPost(payload: CreatePostPayload): Promise<PostRow> {
  console.log('[FeedService:createPost] Payload recebido:', payload);
  // A validação existente já cobre a necessidade de algum conteúdo ou tag
  const hasContent = !!payload.content_text?.trim();
  const hasMedia = !!payload.image_url || !!payload.video_url;
  const hasTags = !!payload.tags && payload.tags.length > 0;
  if (!payload.user_id || (!hasContent && !hasMedia && !hasTags)) {
      throw new Error('Usuário, conteúdo/mídia ou pelo menos uma tag são obrigatórios.');
  }
  
  const { tags, ...postData } = payload;

  try {
    // 1. Inserir o post principal
    console.log('[FeedService:createPost] Inserindo post principal:', postData);
    const { data: newPost, error: postError } = await supabase
      .from('posts')
      .insert([postData])
      .select()
      .single();

    if (postError) {
      console.error('[FeedService:createPost] Erro Supabase ao criar post:', postError);
      throw postError;
    }
    if (!newPost) {
      throw new Error('Falha ao criar o post, nenhum dado retornado.');
    }
    console.log('[FeedService:createPost] Post principal criado, ID:', newPost.id);

    // <<< Adicionar chamada de gamificação após criar o post principal >>>
    const gamificationResult = await processAction({
        supabase: supabase,
        userId: newPost.user_id,
        actionKey: XP_ACTIONS.CREATE_POST,
        sourceId: newPost.id,
        details: { 
            has_content: !!newPost.content_text,
            has_image: !!newPost.image_url,
            has_video: !!newPost.video_url,
            tag_count: tags?.length || 0
        }
    });
    if (!gamificationResult.success) {
        // Logar aviso se a gamificação falhar, mas não interromper a criação do post
        console.warn(`Gamification warning (createPost ${newPost.id}): ${gamificationResult.error}`);
    } else {
        console.log(`Gamification: User ${newPost.user_id} gained ${gamificationResult.xpGained} XP for creating post ${newPost.id}`);
    }
    // <<< Fim da chamada de gamificação >>>

    // 2. Inserir tags, se houver
    if (tags && tags.length > 0) {
      console.log(`[FeedService:createPost] Inserindo ${tags.length} tags para o post ${newPost.id}...`);
      // Mapear para o formato da tabela post_tags
      const tagsToInsert = tags.map(tag => ({
        post_id: newPost.id,
        tag_type: tag.type,
        // Condicional para game_id vs tag_name
        game_id: tag.type === 'game' ? tag.value : null,
        tag_name: tag.type !== 'game' ? tag.value : null 
      }));
      
      console.log('[FeedService:createPost] Tags para inserir:', tagsToInsert);
      const { error: tagsError } = await supabase
        .from('post_tags')
        .insert(tagsToInsert);

      if (tagsError) {
        // Idealmente, deveríamos tentar deletar o post principal aqui (rollback manual)
        // Ou usar uma função SQL transacional. Por enquanto, apenas logamos o erro.
        console.error(`[FeedService:createPost] Erro ao inserir tags para o post ${newPost.id}:`, tagsError);
      } else {
        console.log(`[FeedService:createPost] Tags inseridas com sucesso para o post ${newPost.id}.`);
      }
    }

    return newPost as PostRow;

  } catch (err) {
    console.error('[FeedService:createPost] Exceção geral:', err);
    if (err instanceof Error) { throw err; } 
    else { throw new Error('Ocorreu uma exceção desconhecida ao criar o post.'); }
  }
}

/**
 * Busca comentários para um post específico usando a função RPC.
 */
export async function getComments(postId: string): Promise<CommentWithAuthor[] | null> { 
  if (!postId) {
    console.error('[FeedService:getComments] postId é obrigatório.');
    return null;
  }
  try { 
    console.log(`[FeedService:getComments] Chamando RPC get_comments_for_post para postId: ${postId}`);
    const { data, error } = await supabase
      .rpc('get_comments_for_post', { p_post_id: postId });

    if (error) {
      console.error('[FeedService:getComments] Erro RPC:', error);
      return null; 
    }
    
    console.log(`[FeedService:getComments] RPC retornou ${data?.length ?? 0} comentários.`);
    return data as CommentWithAuthor[] | null;

    } catch (err) {
    console.error('[FeedService:getComments] Exceção:', err);
    return null;
  }
}

/**
 * Adiciona ou remove um like de um post e lida com a gamificação.
 * Retorna o novo estado do like e a contagem atualizada.
 */
export async function toggleLikePost(
  postId: string, 
  userId: string, 
  authorId?: string | null // <<< Adicionar authorId opcional
): Promise<{ liked: boolean; likeCount: number }> {
  if (!postId || !userId) {
    throw new Error('Post ID e User ID são obrigatórios para dar like.');
  }

  try {
    // 1. Verifica o estado atual do like
    const { data: likeEntry, error: checkError } = await supabase
      .from('post_likes')
      .select('created_at') // Seleciona qualquer coluna para verificar existência
      .eq('post_id', postId)
      .eq('user_id', userId)
      .maybeSingle();

    if (checkError) {
      console.error('[FeedService:toggleLikePost] Erro ao verificar like existente:', checkError);
      throw checkError;
    }

    const currentlyLiked = !!likeEntry;
    let success = false;
    let actionWasLike = false;

    // 2. Adiciona ou remove o like
    if (currentlyLiked) {
      console.log(`[FeedService:toggleLikePost] Removendo like para post ${postId} por user ${userId}`);
      success = await removeLikeInternal(postId, userId);
      actionWasLike = false;
    } else {
      console.log(`[FeedService:toggleLikePost] Adicionando like para post ${postId} por user ${userId}`);
      success = await addLikeInternal(postId, userId);
      actionWasLike = true;
    }

    if (!success) {
      throw new Error(`Falha ao ${actionWasLike ? 'adicionar' : 'remover'} o like.`);
    }

    // 3. Se a ação foi ADICIONAR um like e teve sucesso, processar gamificação
    if (actionWasLike && success) {
      // Dar XP para quem deu o like
      try {
        console.log(`[FeedService:toggleLikePost] Processando XP LIKE_POST para user ${userId}`);
        await processAction({
          supabase: supabase,
          userId: userId,
          actionKey: XP_ACTIONS.LIKE_POST,
          sourceId: postId
        });
      } catch (xpError) {
        console.warn(`[FeedService:toggleLikePost] Falha ao processar XP LIKE_POST para user ${userId}:`, xpError);
        // Não lançar erro, continuar mesmo assim
      }

      // Dar XP para quem recebeu o like (se authorId foi fornecido e é diferente)
      if (authorId && authorId !== userId) {
        try {
          console.log(`[FeedService:toggleLikePost] Processando XP RECEIVE_LIKE para author ${authorId}`);
          await processAction({
            supabase: supabase,
            userId: authorId,
            actionKey: XP_ACTIONS.RECEIVE_LIKE,
            sourceId: postId,
            details: { liker_user_id: userId } // Informa quem deu o like
          });
        } catch (xpError) {
          console.warn(`[FeedService:toggleLikePost] Falha ao processar XP RECEIVE_LIKE para author ${authorId}:`, xpError);
          // Não lançar erro, continuar mesmo assim
        }
      } else if (!authorId) {
          console.warn(`[FeedService:toggleLikePost] authorId não fornecido para post ${postId}, não é possível dar XP RECEIVE_LIKE.`);
      }
    }

    // 4. Buscar a contagem atualizada de likes (pode vir da RPC add/remove se ela retornar, ou buscar separadamente)
    // Vamos buscar separadamente para garantir consistência
    const { count, error: countError } = await supabase
        .from('post_likes')
        .select('*' , { count: 'exact', head: true })
        .eq('post_id', postId);

    if (countError) {
        console.error('[FeedService:toggleLikePost] Erro ao buscar contagem de likes:', countError);
        // Retornar o estado esperado da UI, mas talvez com contagem -1 para indicar erro?
        return { liked: !currentlyLiked, likeCount: -1 }; 
    }

    console.log(`[FeedService:toggleLikePost] Sucesso. Novo estado liked: ${!currentlyLiked}, Nova contagem: ${count ?? 0}`);
    return { liked: !currentlyLiked, likeCount: count ?? 0 };

  } catch (err) {
    console.error('[FeedService:toggleLikePost] Exceção:', err);
    // Tentar retornar um estado que não quebre a UI, mas indicar falha
    // O estado 'liked' pode ser revertido no componente que chamou
    throw err; // Re-lança o erro para o chamador tratar (ex: reverter estado da UI)
  }
}

/**
 * Adiciona um comentário a um post.
 */
export async function addComment(payload: AddCommentPayload): Promise<PostRow> {
    console.log('[FeedService:addComment] Payload recebido:', payload);
    if (!payload.post_id || !payload.user_id || !payload.content?.trim()) {
        throw new Error('post_id, user_id e conteúdo são obrigatórios.');
    }

    try {
        // 1. Inserir o comentário
        const { data: newComment, error: commentError } = await supabase
            .from('posts')
            .insert([{
                parent_post_id: payload.post_id,
                user_id: payload.user_id,
                content_text: payload.content,
                // Outros campos como image_url, video_url podem ser null ou omitidos
            }])
            .select()
            .single();

        if (commentError) {
            console.error('[FeedService:addComment] Erro Supabase ao criar comentário:', commentError);
            throw commentError;
        }
        if (!newComment) {
            throw new Error('Falha ao criar o comentário, nenhum dado retornado.');
        }
        console.log('[FeedService:addComment] Comentário criado, ID:', newComment.id);

        // 2. Atualizar contagem de comentários no post pai (considerar trigger/função no banco)
        const { error: updateError } = await supabase.rpc('increment_comment_count' as any, { p_post_id: payload.post_id });
        if (updateError) {
            console.warn('[FeedService:addComment] Erro ao incrementar contagem de comentários:', updateError);
            // Não lançar erro aqui, o comentário foi criado
        }

        // 3. Chamar gamificação
        processAction({
            supabase: supabase,
            userId: payload.user_id,
            actionKey: XP_ACTIONS.COMMENT_POST,
            sourceId: newComment.id, // O source é o próprio comentário
            details: { parent_post_id: payload.post_id }
        }).catch(err => console.warn(`[Gamification Warning] Failed processAction for COMMENT_POST on comment ${newComment.id}:`, err));


        // Retornar o comentário criado
        // A busca completa com autor é feita pela UI se necessário
        return newComment as PostRow;

    } catch (err) {
        console.error('[FeedService:addComment] Exceção:', err);
        throw err; // Re-lançar o erro para o chamador
    }
}

/**
 * Exclui um comentário (um post filho).
 * A RLS do Supabase deve garantir que apenas o autor ou um admin possa excluir.
 */
export async function deleteComment(commentId: string): Promise<boolean> {
  if (!commentId) {
    console.error('[FeedService:deleteComment] commentId é obrigatório.');
    return false;
  }

  let parentId: string | null = null;

  try {
    console.log(`[FeedService:deleteComment] Tentando excluir comentário ID: ${commentId}`);

    // 1. Buscar o parent_post_id ANTES de deletar
    const { data: commentData, error: fetchError } = await supabase
      .from('posts')
      .select('parent_post_id')
      .eq('id', commentId)
      .maybeSingle();

    if (fetchError) {
      console.error('[FeedService:deleteComment] Erro ao buscar dados do comentário:', fetchError);
      return false; // Falha ao buscar, não continuar
    }

    // Se o comentário não existe, considerar sucesso (idempotente)
    if (!commentData) {
        console.log(`[FeedService:deleteComment] Comentário ID: ${commentId} não encontrado para exclusão.`);
        return true;
    }

    parentId = commentData.parent_post_id; // Armazena o ID do pai, se houver

    // 2. Deletar o comentário
    const { error: deleteError } = await supabase
      .from('posts')
      .delete()
      .match({ id: commentId });

    if (deleteError) {
      console.error('[FeedService:deleteComment] Erro Supabase ao excluir:', deleteError);
      return false;
    }

    console.log(`[FeedService:deleteComment] Comentário ID: ${commentId} excluído com sucesso.`);

    // 3. Decrementar contador do pai, SE houver pai
    if (parentId) {
      console.log(`[FeedService:deleteComment] Decrementando contador para o post pai ID: ${parentId}`);
      const { error: decrementError } = await supabase.rpc('decrement_comment_count', { p_post_id: parentId });
      
      if (decrementError) {
          // Logar o erro mas considerar a exclusão do comentário um sucesso
          console.warn(`[FeedService:deleteComment] Erro ao decrementar contador para post pai ${parentId}:`, decrementError);
      }
    }

    return true; // Exclusão bem-sucedida

  } catch (err) {
    console.error('[FeedService:deleteComment] Exceção:', err);
    return false;
    }
  }

  /**
 * Exclui um post principal.
 * A RLS do Supabase deve garantir que apenas o autor ou um admin possa excluir.
 * Supabase deve lidar com a exclusão em cascata de comentários e likes (via constraints ou RLS).
 */
export async function deletePost(postId: string, isAdminUser?: boolean): Promise<boolean> {
  if (!postId) {
    console.error('[FeedService:deletePost] postId é obrigatório.');
    return false;
  }
  try {
    console.log(`[FeedService:deletePost] Tentando excluir post ID: ${postId}, isAdmin: ${!!isAdminUser}`);

    if (isAdminUser) {
      // Administrador chama a função RPC que bypassa RLS (devido a SECURITY DEFINER)
      console.log(`[FeedService:deletePost] Usuário é admin. Chamando RPC delete_post_as_admin.`);
      const { error: rpcError } = await supabase.rpc('delete_post_as_admin', {
        post_id_to_delete: postId
      });

      if (rpcError) {
        console.error('[FeedService:deletePost] Erro RPC delete_post_as_admin:', rpcError);
        // O toast pode ser melhorado para dar feedback específico do erro da RPC
        // toast.error(rpcError.message || 'Falha ao excluir post como admin.'); 
        return false;
      }
      console.log(`[FeedService:deletePost] Post ID: ${postId} excluído com sucesso via RPC admin.`);
      return true;

    } else {
      // Usuário não-admin (deleção normal sujeita à RLS de autoria)
      console.log(`[FeedService:deletePost] Usuário não é admin. Tentando exclusão padrão.`);
      const { error } = await supabase
        .from('posts')
        .delete()
        .match({ id: postId }); // RLS (uid() = user_id) deve estar ativa para esta operação

      if (error) {
        console.error('[FeedService:deletePost] Erro Supabase ao excluir post (não-admin):', error);
        return false;
      }
      console.log(`[FeedService:deletePost] Post ID: ${postId} excluído com sucesso (não-admin) ou não existia.`);
      return true;
    }

  } catch (err) {
    console.error('[FeedService:deletePost] Exceção:', err);
    return false;
  }
}

/**
 * Busca um único post pelo seu ID, consultando as tabelas individualmente
 * e montando a estrutura GetFeedResult.
 */
export async function getPostById(
  postId: string,
  loggedUserId: string | null
): Promise<GetFeedResult | null> {
  if (!postId) {
    console.error('[FeedService:getPostById] postId é obrigatório.');
    return null;
  }

  try {
    // 1. Buscar o post principal
    const { data: postData, error: postError } = await supabase
      .from('posts')
      .select('*')
      .eq('id', postId)
      .maybeSingle(); // Use maybeSingle() para retornar null se não encontrado

    if (postError) {
      console.error('[FeedService:getPostById] Erro ao buscar post:', postError);
      throw postError;
    }

    if (!postData) {
      console.log(`[FeedService:getPostById] Post ID: ${postId} não encontrado na tabela posts.`);
      return null; // Post não existe
    }

    // 2. Buscar perfil do autor
    const { data: authorProfile, error: profileError } = await supabase
      .from('profiles')
      .select('username, full_name, avatar_url')
      .eq('id', postData.user_id)
      .single();

    if (profileError) {
      console.error('[FeedService:getPostById] Erro ao buscar perfil do autor:', profileError);
      // Continuar mesmo sem perfil? Ou retornar erro? Por enquanto, continuar.
    }

    // 3. Contar likes e verificar like do usuário logado
    let likeCount = 0;
    let userLiked = false;
    const { count: likesCountData, error: likesCountError } = await supabase
      .from('post_likes')
      .select('*' , { count: 'exact', head: true })
      .eq('post_id', postId);
      
    if (likesCountError) {
       console.error('[FeedService:getPostById] Erro ao contar likes:', likesCountError);
    } else {
        likeCount = likesCountData ?? 0;
    }
    
    if (loggedUserId) {
        const { data: userLikeData, error: userLikeError } = await supabase
            .from('post_likes')
            .select('post_id')
            .eq('post_id', postId)
            .eq('user_id', loggedUserId)
            .maybeSingle();
            
        if (userLikeError) {
            console.error('[FeedService:getPostById] Erro ao verificar like do usuário:', userLikeError);
        } else {
            userLiked = !!userLikeData; // True se o registro existir
        }
    }

    // 4. Contar comentários (posts onde parent_post_id é o postId atual)
    let commentCount = 0;
    const { count: commentCountData, error: commentCountError } = await supabase
        .from('posts')
        .select('id', { count: 'exact', head: true })
        .eq('parent_post_id', postId);
        
    if (commentCountError) {
        console.error('[FeedService:getPostById] Erro ao contar comentários:', commentCountError);
    } else {
        commentCount = commentCountData ?? 0;
    }

    // 5. Buscar tags associadas
    const { data: tagsData, error: tagsError } = await supabase
      .from('post_tags')
      .select(`
        *,
        games ( name )
      `)
      .eq('post_id', postId);

    if (tagsError) {
        console.error('[FeedService:getPostById] Erro ao buscar tags:', tagsError);
    }

    // Mapear tags
    const formattedTags = tagsData?.map(tag => {
      const game = tag.games as { name: string } | null;
      return {
          id: tag.id, // Garantir que todas as propriedades de PostTagRow existam
          created_at: tag.created_at,
          post_id: tag.post_id,
          // <<< Correção: Asserção de tipo para tag_type >>>
          tag_type: tag.tag_type as PostTagRow['tag_type'], 
          game_id: tag.game_id,
          tag_name: tag.tag_name,
          game_name: tag.tag_type === 'game' ? (game?.name || null) : null,
      } as PostTagRow; // Asserção final para garantir conformidade com PostTagRow
    }) || null;


    // 6. Montar o objeto GetFeedResult
    const result: GetFeedResult = {
      id: postData.id,
      created_at: postData.created_at,
      updated_at: postData.updated_at,
      content_text: postData.content_text,
      image_url: postData.image_url,
      video_url: postData.video_url,
      user_id: postData.user_id,
      author_username: authorProfile?.username || null,
      author_full_name: authorProfile?.full_name || null,
      author_avatar_url: authorProfile?.avatar_url || null,
      like_count: likeCount,
      comment_count: commentCount,
      user_liked: userLiked,
      tags: formattedTags, // Usar tags formatadas
    };

    console.log(`[FeedService:getPostById] Post ID: ${postId} dados montados com sucesso.`);
    return result;

  } catch (err) {
    console.error(`[FeedService:getPostById] Exceção geral para postId ${postId}:`, err);
    return null;
  }
}

// Outros métodos podem ser adicionados aqui (ex: deleteComment, editComment)